import type React from 'react';
import { useEffect, useState } from 'react';
import { type FormFactor, getMinimizedChatViewText } from '../config/constants';
import { getDeploymentTarget, isChromeIndicatorHidden } from '../config/deployment';
import { useChatLanguage } from '../context/ChatThemeContext';
import ArrowUpIcon from './ArrowUpIcon';
import GlassContainer from './GlassContainer';
import SparkleIcon from './SparkleIcon';

interface MinimizedChatViewProps {
    onClick: () => void;
    primaryColor: string;
    siteConfig: { name: string };
    zIndex?: number; // Custom z-index from site config - defaults to 9999
    formFactor?: FormFactor; // Widget form factor - 'default' or 'copilot'
    viewLayout?: 'expanded' | 'copilot';
}

const MinimizedChatView: React.FC<MinimizedChatViewProps> = ({
    onClick,
    primaryColor,
    zIndex = 9999,
    formFactor = 'default',
    viewLayout = 'expanded',
}) => {
    // Get the effective language using the theme context
    const effectiveLanguage = useChatLanguage();

    // Get localized button text - use copilot text for copilot form factor
    const askButtonText = getMinimizedChatViewText(
        formFactor === 'copilot' ? 'ASK_BUTTON_COPILOT' : 'ASK_BUTTON',
        effectiveLanguage
    );

    // Chrome extension visual indicator
    const isChromeExtension = getDeploymentTarget() === 'chrome-extension';
    const showChromeIndicator = isChromeExtension && !isChromeIndicatorHidden();

    // Position follows formFactor for form_assistant (always right), otherwise follows viewLayout
    const useRightPosition = formFactor === 'copilot' || viewLayout === 'copilot';

    // Slide-in entrance animation. Copilot mode slides from the right; default mode
    // scrolls up from below to mirror CollapsedChatView's main-widget reveal.
    const [hasAnimated, setHasAnimated] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setHasAnimated(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const entranceTransform = useRightPosition
        ? hasAnimated
            ? 'translateX(0)'
            : 'translateX(calc(100% + 4rem))'
        : hasAnimated
          ? 'translateX(-50%) translateY(0)'
          : 'translateX(-50%) translateY(calc(100% + 4rem))';

    return (
        <div
            className="chat-widget-container"
            data-ph-capture-attribute-widget-type="minimized-chat"
            data-ph-capture-attribute-form-factor={formFactor}
            style={{
                position: 'fixed',
                bottom: '32px',
                // Copilot: position at bottom-right; Default: center
                ...(useRightPosition ? { right: '32px' } : { left: '50%' }),
                transform: entranceTransform,
                opacity: hasAnimated ? 1 : 0,
                transition: 'transform 500ms ease-out, opacity 500ms ease-out',
                zIndex,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 'auto',
                height: 'auto',
                pointerEvents: 'auto', // Ensure clicks work in Shadow DOM with pointer-events: none parent
            }}
        >
            <style>{`
                .chat-cta-ask-button {
                    background-color: var(--rw-surface-input);
                }

                .chat-cta-ask-button:hover {
                    background-color: var(--rw-surface-interactive-elevated-hover);
                    box-shadow: var(--rw-shadow-interactive-hover);
                }
            `}</style>
            <GlassContainer
                className="minimized-chat-glass-container relative min-h-14 transition-transform duration-300 ease-out hover:-translate-y-0.5"
                borderRadius="var(--rw-radius-shell-compact)"
                padding="8px"
                showShadow={false}
                style={
                    showChromeIndicator
                        ? {
                              boxShadow: '0 0 0 3px #dc2626, 0 0 12px rgba(220, 38, 38, 0.4)',
                          }
                        : undefined
                }
            >
                <button
                    className="chat-cta-ask-button flex items-center gap-2 px-2 py-1 h-10 cursor-pointer transition-all duration-200 ease-out w-auto group"
                    data-ph-capture-attribute-button-type="primary-cta"
                    data-ph-capture-attribute-location="floating-widget"
                    onClick={onClick}
                    type="button"
                    style={{
                        borderRadius: 'var(--rw-radius-control-compact)',
                    }}
                >
                    {/* Sparkle Icon - same as SearchBar */}
                    <SparkleIcon color={primaryColor} size={20} />

                    <span
                        className="chat-cta-text whitespace-nowrap transition-colors duration-200"
                        style={{
                            fontSize: '16px',
                            color: 'var(--rw-text-placeholder)',
                            fontWeight: 'var(--rw-font-weight-placeholder)',
                            letterSpacing: 'normal',
                        }}
                    >
                        {askButtonText}
                    </span>

                    <div>
                        <ArrowUpIcon width={24} height={24} color="var(--rw-text-placeholder)" />
                    </div>
                </button>
            </GlassContainer>
        </div>
    );
};

export default MinimizedChatView;
